/**
 * Pre-flight cost estimate for document analysis.
 *
 * Page count is the only thing we know before an upload starts, so the
 * estimate is deliberately rough and always framed as an upper-ish bound:
 * a scanned title commitment page costs about a page of vision tokens in
 * and a short structured summary out. The point is not accounting — it is
 * giving people a chance to stop before a 50-page closing package burns
 * more credits than they expected.
 */

import { estimateCredits } from "./ai-usage";

/** Model the analyzers run on today; keeps the estimate on the same rate card. */
export const PREFLIGHT_MODEL = "google/gemini-2.5-flash";

/** Rough tokens consumed per page: rasterized page in, structured rows out. */
const PROMPT_TOKENS_PER_PAGE = 1_400;
const COMPLETION_TOKENS_PER_PAGE = 220;
/** Characters per token for pasted / .txt Schedule B text. */
const CHARS_PER_TOKEN = 4;

/** Warn above this many pages, even when the credit estimate looks small. */
export const PREFLIGHT_PAGE_THRESHOLD = 15;
/** Warn above this estimated credit spend for a single document. */
export const PREFLIGHT_CREDIT_THRESHOLD = 0.12;

export interface UploadCostEstimate {
  pages: number | null;
  promptTokens: number;
  completionTokens: number;
  credits: number;
  /** True when this upload is big enough to ask before spending. */
  overThreshold: boolean;
  /** Which rule tripped, for the copy in the dialog. */
  reason: "pages" | "credits" | null;
}

export function estimateUploadCost(input: {
  pages?: number | null;
  textChars?: number | null;
}): UploadCostEstimate {
  const pages = typeof input.pages === "number" && input.pages > 0 ? input.pages : null;

  let promptTokens: number;
  let completionTokens: number;

  if (pages) {
    promptTokens = pages * PROMPT_TOKENS_PER_PAGE;
    completionTokens = pages * COMPLETION_TOKENS_PER_PAGE;
  } else {
    const chars = Math.max(input.textChars ?? 0, 0);
    promptTokens = Math.ceil(chars / CHARS_PER_TOKEN);
    completionTokens = Math.ceil(promptTokens * 0.15);
  }

  const credits = estimateCredits(PREFLIGHT_MODEL, promptTokens, completionTokens);
  const overPages = pages !== null && pages > PREFLIGHT_PAGE_THRESHOLD;
  const overCredits = credits > PREFLIGHT_CREDIT_THRESHOLD;

  return {
    pages,
    promptTokens,
    completionTokens,
    credits,
    overThreshold: overPages || overCredits,
    reason: overPages ? "pages" : overCredits ? "credits" : null,
  };
}

/** One-line plain-English summary used in the confirmation dialog. */
export function describeUploadCost(estimate: UploadCostEstimate): string {
  const size = estimate.pages
    ? `${estimate.pages.toLocaleString()} pages`
    : `${Math.round(estimate.promptTokens / 1000)}k tokens of text`;
  return `About ${estimate.credits.toFixed(3)} AI credits for ${size}.`;
}
